import { ref, computed, watch, type Ref } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { departmentApi, type Department } from '@/entities/Department';
import { fetchAllPages } from '@/shared/lib';

interface UseDepartmentOptionsConfig {
  initialDepartmentId?: Ref<number | undefined>;
}

export function useDepartmentOptions(config?: UseDepartmentOptionsConfig) {
  const departments = ref<Department[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const initialDepartmentLabel = ref<string | undefined>(undefined);

  let requestCounter = 0;

  const departmentOptions = computed(() => {
    return departments.value.map((dept) => ({
      value: dept.id.toString(),
      label: dept.code,
    }));
  });

  async function fetchDepartments(search?: string) {
    isLoading.value = true;
    error.value = null;
    const currentRequest = ++requestCounter;

    try {
      const result = search
        ? (
            await departmentApi.search({
              search,
              limit: 10,
              page: 1,
              sort: 'code',
              order: 'asc',
            })
          ).data
        : await fetchAllPages((page) =>
            departmentApi.search({
              search: '',
              limit: 100,
              page,
              sort: 'code',
              order: 'asc',
            })
          );

      if (currentRequest !== requestCounter) return;

      const initId = config?.initialDepartmentId?.value;
      if (initId) {
        const alreadyInResults = result.some((d) => d.id === initId);
        if (!alreadyInResults) {
          const cached = departments.value.find((d) => d.id === initId);
          if (cached) {
            departments.value = [cached, ...result];
            return;
          }
        }
      }

      departments.value = result;
    } catch {
      if (currentRequest !== requestCounter) return;

      error.value = 'Erro ao carregar departamentos';
    } finally {
      if (currentRequest === requestCounter) {
        isLoading.value = false;
      }
    }
  }

  let latestTerm = '';

  const debouncedFetch = useDebounceFn((term: string) => {
    if (term !== latestTerm) return;
    fetchDepartments(term);
  }, 300);

  function handleSearch(term: string) {
    latestTerm = term;
    if (!term) {
      fetchDepartments();
      return;
    }
    debouncedFetch(term);
  }

  function loadDepartments() {
    return fetchDepartments();
  }

  function syncInitialDepartmentLabel(id: number) {
    const found = departments.value.find((dept) => dept.id === id);
    if (found) {
      initialDepartmentLabel.value = found.code;
      return true;
    }
    return false;
  }

  if (config?.initialDepartmentId) {
    watch(
      config.initialDepartmentId,
      async (newId) => {
        if (!newId) return;

        if (syncInitialDepartmentLabel(newId)) return;

        try {
          const dept = await departmentApi.getById(newId);
          initialDepartmentLabel.value = dept.code;
          const exists = departments.value.some((d) => d.id === dept.id);
          if (!exists) {
            departments.value = [dept, ...departments.value];
          }
        } catch {
          // silently ignore - department will not appear in options
        }
      },
      { immediate: true },
    );

    watch(departments, () => {
      const id = config.initialDepartmentId?.value;
      if (id) {
        syncInitialDepartmentLabel(id);
      }
    });
  }

  return {
    departments,
    departmentOptions,
    isLoading,
    error,
    loadDepartments,
    handleSearch,
    initialDepartmentLabel,
  };
}
